/**
 * PageCountCalculator - Utility for calculating page counts and totals
 * 
 * Extracted from TypesetterWorkerClient for better modularity and testability.
 * 
 * This utility provides functions for calculating total page counts from
 * page indices, which is useful for pagination and cache management.
 */

import type { PageLayout } from '../../../core/types/Typesetter';

/**
 * Calculate total page count from maximum page index
 * 
 * The total page count is the maximum page index + 1 (since page indices are 0-based).
 * This is used to update the total pages count when new pages are added to the cache.
 * 
 * @param maxPageIndex - Maximum page index (0-based)
 * @returns Total page count (maxPageIndex + 1)
 */
export function calculateTotalPagesFromMaxIndex(maxPageIndex: number): number {
  return maxPageIndex + 1;
}

/**
 * Calculate total page count from an array of pages
 * 
 * Finds the maximum page index in the array and calculates the total page count.
 * 
 * @param pages - Array of PageLayout objects
 * @returns Total page count, or 0 if array is empty
 */
export function calculateTotalPagesFromPages(pages: PageLayout[]): number {
  if (pages.length === 0) {
    return 0;
  }
  
  const maxPageIndex = Math.max(...pages.map(p => p.page_index));
  return calculateTotalPagesFromMaxIndex(maxPageIndex);
}

/**
 * Update total page count, keeping the maximum of current and new total
 * 
 * Ensures the total page count never decreases when updating from new pages.
 * 
 * @param currentTotal - Current total page count
 * @param newPages - Array of new PageLayout objects
 * @returns Updated total page count (max of current and calculated from new pages)
 */
export function updateTotalPageCount(
  currentTotal: number,
  newPages: PageLayout[]
): number {
  const newTotal = calculateTotalPagesFromPages(newPages);
  return Math.max(currentTotal, newTotal);
}

